import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import AbstractListView from '@UI/Store/AbstractListView';
import type InterviewView from '@UI/Store/Interviews/Views/InterviewView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `typeKey` and `subjectId` are the scope this list belongs to: `stopOn` compares them (`isFor`)
 * against the subject currently on screen to decide whether the cached page is still valid — the case
 * where the default `useStoreQuery` cache setting (omitting `cache`) applies (`guides/state-stores.md`).
 */
class InterviewsListView extends AbstractListView<InterviewView> {
	readonly typeKey: string;
	readonly subjectId: number;
	readonly totalRecords: number;

	constructor(list: readonly InterviewView[], typeKey: string, subjectId: number, totalRecords: number) {
		super(list, 'InterviewsListView');

		this.typeKey = typeKey;
		this.subjectId = subjectId;
		this.totalRecords = totalRecords;

		Object.freeze(this);
	}

	isFor(subject: SubjectAddress): boolean {
		return this.typeKey === subject.typeKey && this.subjectId === subject.subjectId;
	}

	updateInterview(interview: InterviewView): InterviewsListView {
		return new InterviewsListView(
			this.list.map((entry) => (entry.id === interview.id ? interview : entry)),
			this.typeKey,
			this.subjectId,
			this.totalRecords,
		);
	}

	/** Same instance when no row carries that `id`, so a replayed deletion changes nothing. */
	removeInterview(interviewId: number): InterviewsListView {
		if (this.findById(interviewId) === null) {
			return this;
		}

		return new InterviewsListView(
			this.list.filter((entry) => entry.id !== interviewId),
			this.typeKey,
			this.subjectId,
			this.totalRecords - 1,
		);
	}

	/**
	 * Idempotent by `id`: a replay of the same creation response (the transfer handler retries on
	 * failure and never distinguishes a first creation from a replay) replaces the existing row in
	 * place rather than duplicating it. Only a genuine insertion counts toward `totalRecords` — a
	 * replay changes nothing about how many interviews the server actually holds.
	 *
	 * A new row goes to the head. The list is loaded once, sorted server-side by `createdAt`
	 * descending, and `createdAt` never travels in `InterviewPayload`. Server ids are assigned
	 * monotonically, so the interview that was just created is always the most recent one: `id`
	 * descending is the closest available proxy for "most recent first" without that field.
	 */
	upsertInterview(interview: InterviewView): InterviewsListView {
		if (this.findById(interview.id) !== null) {
			return this.updateInterview(interview);
		}

		return new InterviewsListView(
			[interview, ...this.list],
			this.typeKey,
			this.subjectId,
			this.totalRecords + 1,
		);
	}
}

export default InterviewsListView;
